import { CheckCircle2, ChevronRight, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { ErrorState, Skeleton } from '@/components/ui/Feedback';
import { ApiError } from '@/lib/api';
import { DIVISION_PATH, useProduct } from '@/features/catalog/api';
import { ProductCard } from '@/features/catalog/ProductCard';
import { ProductVisual } from '@/features/catalog/ProductVisual';
import { Seo } from '@/features/site/Seo';
import { useSite } from '@/features/site/SiteProvider';
import { InquirySection } from '@/features/site/sections';
import { cn, whatsappLink } from '@/lib/utils';
import NotFoundPage from './NotFoundPage';

export default function ProductPage() {
  const { slug } = useParams();
  const product = useProduct(slug);
  const { contact } = useSite().settings;
  const [imageIndex, setImageIndex] = useState(0);

  if (product.isError && product.error instanceof ApiError && product.error.status === 404) return <NotFoundPage />;
  if (product.isError) {
    return (
      <div className="container-page py-16">
        <ErrorState error={product.error} onRetry={() => void product.refetch()} />
      </div>
    );
  }
  if (product.isPending) {
    return (
      <div className="container-page grid gap-10 py-12 lg:grid-cols-2" aria-busy="true">
        <Skeleton className="aspect-[4/3] rounded-[20px]" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  const p = product.data;
  const divisionPath = DIVISION_PATH[p.category.division];
  const divisionName = p.category.division === 'APPAREL' ? 'Apparel' : 'Sports Goods';
  const activeImage = p.images[imageIndex];

  return (
    <>
      <Seo title={p.seoTitle || p.name} description={p.seoDescription || p.summary || undefined} path={`/products/${p.slug}`} image={p.images[0]?.url} />

      <section className="bg-surface pt-10 pb-16 lg:pt-14 lg:pb-24">
        <div className="container-page">
          <nav aria-label="Breadcrumb" className="text-[13px] text-muted">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-primary">
                  Home
                </Link>
              </li>
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              <li>
                <Link to={divisionPath} className="hover:text-primary">
                  {divisionName}
                </Link>
              </li>
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              <li>
                <Link to={`${divisionPath}/${p.category.slug}`} className="hover:text-primary">
                  {p.category.name}
                </Link>
              </li>
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              <li aria-current="page" className="text-ink-soft">
                {p.name}
              </li>
            </ol>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-16">
            {/* gallery */}
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-[8px]">
                {activeImage ? (
                  <img src={activeImage.url} alt={activeImage.altText} className="aspect-[4/3] w-full object-cover" />
                ) : (
                  <ProductVisual product={p} className="aspect-[4/3]" eager />
                )}
              </div>
              {p.images.length > 1 && (
                <ul className="mt-3 flex gap-2 overflow-x-auto" aria-label="Product images">
                  {p.images.map((img, i) => (
                    <li key={img.id}>
                      <button
                        type="button"
                        onClick={() => setImageIndex(i)}
                        aria-label={`Show image ${i + 1}: ${img.altText}`}
                        aria-current={i === imageIndex}
                        className={cn('h-16 w-20 overflow-hidden rounded-[6px] border-2', i === imageIndex ? 'border-accent' : 'border-transparent')}
                      >
                        <img src={img.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="lg:col-span-5">
              <p className="eyebrow flex items-center gap-3 text-accent-dark">
                <span aria-hidden className="h-px w-8 bg-accent" />
                {p.category.name}
              </p>
              <h1 className="display-md mt-4 text-primary">{p.name}</h1>
              {p.summary && <p className="lede mt-4">{p.summary}</p>}

              <dl className="mt-8 divide-y divide-line border-y border-line-strong/70">
                {p.moq && (
                  <div className="flex justify-between gap-6 py-3.5 text-[15px]">
                    <dt className="text-muted">Minimum order (MOQ)</dt>
                    <dd className="font-semibold text-primary">{p.moq}</dd>
                  </div>
                )}
                {p.specifications.map((s) => (
                  <div key={s.label} className="flex justify-between gap-6 py-3.5 text-[15px]">
                    <dt className="text-muted">{s.label}</dt>
                    <dd className="text-right font-semibold text-primary">{s.value}</dd>
                  </div>
                ))}
              </dl>

              {p.customizationOptions.length > 0 && (
                <div className="mt-8">
                  <h2 className="eyebrow font-sans text-muted">Customisation options</h2>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {p.customizationOptions.map((c) => (
                      <li key={c} className="inline-flex items-center gap-1.5 rounded-[4px] bg-white px-3 py-1.5 text-[13.5px] font-medium text-ink-soft shadow-[0_1px_2px_rgba(11,23,38,0.06)]">
                        <CheckCircle2 className="h-3.5 w-3.5 text-accent" aria-hidden /> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {p.description && <div className="mt-8 text-[15.5px] leading-[1.75] whitespace-pre-line text-ink-soft">{p.description}</div>}

              <div className="mt-10 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <ButtonLink to="#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
                  Request a quote for this product
                </ButtonLink>
                {contact.whatsappNumber && (
                  <a
                    href={whatsappLink(contact.whatsappNumber, `Hello, I'm interested in ${p.name}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-[52px] items-center justify-center gap-2 rounded-[6px] border border-line-strong bg-white px-5 text-[15px] font-semibold hover:border-ink"
                  >
                    <MessageCircle className="h-4 w-4 text-whatsapp" aria-hidden /> Ask on WhatsApp
                  </a>
                )}
              </div>
              <p className="mt-5 text-[13.5px] text-muted">B2B manufacturing only — made to order with your branding. Prices are quoted per order.</p>
            </div>
          </div>
        </div>
      </section>

      {p.related.length > 0 && (
        <section className="section-y bg-white" aria-labelledby="related-title">
          <div className="container-page">
            <h2 id="related-title" className="display-md text-primary">
              More in {p.category.name}
            </h2>
            <div className="mt-10 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {p.related.map((r) => (
                <ProductCard key={r.id} product={r} />
              ))}
            </div>
          </div>
        </section>
      )}

      <InquirySection context={{ categorySlug: p.category.slug, product: { slug: p.slug, name: p.name } }} heading={`Get a quote for ${p.name}`} />
    </>
  );
}
